import styles from './PricingPagePc.module.less'
import { commonUtils } from '@/helpers/commonUtils'
import AIcon from '@/components/A/AIcon/AIcon'
import { useRequest } from 'alova/client'
import { api } from '@/api'
import { subscribeService } from '@/services/subscribeService'
import { Trans, useTranslation } from 'react-i18next'
import { observer } from 'mobx-react'
import { userService } from '@/services/userService'
import { PAGE_LINKS } from '@/router/pageConst'
import { useNavigate } from 'react-router-dom'
import MiddleText from '@/components/MiddleText/MiddleText'
import { INTERNAL_HOST } from '@/commonConst'
import { message } from 'antd'
import { useState } from 'react'
import classNames from 'classnames'

type SecondaryTabKey = 'quick' | 'diagnosis' | 'insight'
const QUICK_STARTER_CATALOG_KEY = 'QuickStarter'
const ENABLE_FASTSPRING_CHECKOUT = false

function PricingPagePc() {
  const { t, i18n } = useTranslation()
  const isInternal = INTERNAL_HOST.includes(location.hostname)
  const isZhLocale = (i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('zh')
  const isEnLocale = (i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('en')
  const isJaLocale = (i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('ja')
  const perTestUnit = t('pricing.premium.time', { defaultValue: isZhLocale ? '次' : 'time' })
  const [primaryTab, setPrimaryTab] = useState<'landscape' | 'portrait'>('landscape')
  const [secondaryTab, setSecondaryTab] = useState<SecondaryTabKey>('quick')
  const isQuickPricing = secondaryTab === 'quick'
  const formatNumber = (value: string | number) => {
    if (typeof value === 'number') {
      return value.toLocaleString('en-US')
    }
    return value
  }
  const formatCurrency = (value: string | number) => (isInternal ? `CNY¥${formatNumber(value)}` : `US$${formatNumber(value)}`)
  const formatPricePerTest = (value: string | number) => {
    const currencyText = `${isInternal ? '¥' : '$'}${formatNumber(value)}`
    if (isJaLocale) return currencyText
    if (isEnLocale) return `${currencyText} per ${perTestUnit}`
    return `${currencyText}/${perTestUnit}`
  }
  const navigate = useNavigate()
  const { data: planData } = useRequest(api.pricing.getPlanList)
  const starterPlan = planData?.catalogs?.Starter
  const quickStarterPlan = planData?.catalogs?.[QUICK_STARTER_CATALOG_KEY]
  const activeStarterPlan = isQuickPricing ? quickStarterPlan : starterPlan
  const starterPrice = activeStarterPlan?.price
  const starterAnalyseCount = activeStarterPlan?.adAnalyseCount
  const starterPricePerTest = starterPrice && starterAnalyseCount
    ? Math.floor(starterPrice / starterAnalyseCount)
    : '-'
  const starterOriginalPrice = isQuickPricing
    ? quickStarterPlan?.originalPrice ||
      (quickStarterPlan?.price ? quickStarterPlan.price * 2 : undefined)
    : starterPrice
      ? starterPrice * 2
      : undefined
  const starterOriginalPriceText = !starterOriginalPrice
    ? '-'
    : formatCurrency(starterOriginalPrice)
  const starterPricePerTestDesc = starterPricePerTest === '-'
    ? '-'
    : formatPricePerTest(starterPricePerTest)
  const planBenefits = [
    t('pricing.starter.desc2'),
    t('pricing.starter.desc3'),
    t('pricing.starter.desc4'),
    t('pricing.starter.desc5'),
    t('pricing.starter.desc6'),
    t('pricing.benefits.expertSupport', { defaultValue: '专家团队提供支持和指导' }),
  ]
  const starterCardDesc = [
    t('pricing.starter.cardDesc1', {
      pricePerTest: starterPricePerTestDesc,
      defaultValue: '限时折扣，仅需{{pricePerTest}}',
    }),
    t('pricing.starter.cardDesc2', { defaultValue: '次数永久有效' }),
  ]
  const quickUnlimitedPrice = isInternal ? '50,000' : '7,250'
  const quickUnlimitedTestCount = '500'
  const quickUnlimitedPricePerTest = isInternal ? 100 : 14.5
  const unlimitedPrice = isQuickPricing ? quickUnlimitedPrice : isInternal ? '200,000' : '29,950'
  const unlimitedTestCount = isQuickPricing ? quickUnlimitedTestCount : '1,000'
  const unlimitedPlanDesc = isQuickPricing
    ? t('pricing.unlimited.planDesc500', {
      defaultValue: isZhLocale ? '全年畅享500次测试' : 'Enjoy up to 500 tests throughout the year',
    })
    : t('pricing.unlimited.planDesc1000', { defaultValue: '全年畅享1,000次测试' })
  const unlimitedCardDesc = [
    t('pricing.unlimited.cardDesc1', {
      pricePerTest: isQuickPricing
        ? formatPricePerTest(quickUnlimitedPricePerTest || '-')
        : isInternal
          ? formatPricePerTest(200)
          : formatPricePerTest(29.95),
      defaultValue: '低至{{pricePerTest}}',
    }),
    t('pricing.unlimited.cardDesc2', { defaultValue: '次数有效期为一年' }),
    t('pricing.unlimited.cardDesc3', { defaultValue: '定制自有基准值' }),
  ]
  const customPlanPrefix = t('pricing.benefits.customPlanPrefix', {
    defaultValue: '我们支持多种灵活与定制化方案——欢迎联系我们',
  })
  const customPlanSuffix = t('pricing.benefits.customPlanSuffix', {
    defaultValue: '获取专属方案',
  })
  const secondaryTabs = primaryTab === 'landscape'
    ? [
      { key: 'quick' as const, label: t('productSwitch.modal.cards.tvcQuick.title'), icon: 'rocket' },
      { key: 'diagnosis' as const, label: t('productSwitch.modal.cards.tvcBase.title'), icon: 'video' },
      { key: 'insight' as const, label: t('productSwitch.modal.cards.tvcSurvey.title'), icon: 'brain' },
    ]
    : [
      { key: 'quick' as const, label: t('productSwitch.modal.cards.portraitQuick.title'), icon: 'rocket' },
      { key: 'diagnosis' as const, label: t('productSwitch.modal.cards.portraitBase.title'), icon: 'video' },
      { key: 'insight' as const, label: t('productSwitch.modal.cards.portraitSurvey.title'), icon: 'brain' },
    ]
  const planCardsAnimationKey = `${primaryTab}-${secondaryTab}`

  const handleContactUs = () => {
    navigate(PAGE_LINKS.contact)
  }

  const dataPopupClosed = () => {
    console.log('dataPopupClosed')
  }
  const dataPopupWebhookReceived = () => {
    console.log('dataPopupWebhookReceived')
  }
  const dataErrorCallback = () => {
    message.error('call fastspringerror')
  }

  const handleSubscribe = () => {
    if (isQuickPricing) {
      handleContactUs()
      return
    }
    if (isInternal) {
      handleContactUs()
      return
    }
    if (!userService.userInfo) {
      navigate(PAGE_LINKS.login)
      return
    }

    const plan = starterPlan
    if (!plan?.planId) {
      message.error('Plan is unavailable, please try again later.')
      return
    }
    subscribeService.subscribePlan(plan.planId, {
      dataPopupClosed,
      dataPopupWebhookReceived,
      dataErrorCallback,
    })
  }

  return (
    <div className={styles.pricingPage} data-testid='pricing-page'>
      <div className={styles.pricingPageContent}>
        <img src={commonUtils.getImagePath('pricingPage/pricing-page-bg.png')} alt='' />
        <p className={classNames(styles.contentTitle, {
          [styles.contentTitleInternal]: isInternal,
        })}>
          <span className={styles.weight}>AdEff - </span>
          {t('pricing.title')}
        </p>
        <div className={styles.primaryTabWrap}>
          <div className={styles.primaryTab}>
            <button
              data-testid='pricing-primary-tab-landscape'
              className={classNames(styles.primaryTabItem, {
                [styles.primaryTabItemActive]: primaryTab === 'landscape',
              })}
              onClick={() => setPrimaryTab('landscape')}
            >
              {t('productSwitch.modal.tabs.tvc')}
            </button>
            <button
              data-testid='pricing-primary-tab-portrait'
              className={classNames(styles.primaryTabItem, {
                [styles.primaryTabItemActive]: primaryTab === 'portrait',
              })}
              onClick={() => setPrimaryTab('portrait')}
            >
              {t('productSwitch.modal.tabs.portrait')}
            </button>
          </div>
        </div>
        <div className={styles.secondaryTabWrap}>
          {secondaryTabs.map((tab) => {
            const isActive = secondaryTab === tab.key
            const iconName = isActive ? tab.icon : `${tab.icon}-disabled`
            return (
              <button
                key={tab.key}
                data-testid={`pricing-secondary-tab-${tab.key}`}
                className={classNames(styles.secondaryTabItem, {
                  [styles.secondaryTabItemActive]: isActive,
                })}
                onClick={() => setSecondaryTab(tab.key)}
              >
                <img
                  className={styles.secondaryTabIcon}
                  src={commonUtils.getImagePath(`productSwitchModal/${iconName}.png`)}
                  alt=''
                />
                {tab.label}
              </button>
            )
          })}
        </div>
        <div className={styles.pricingBox}>
          <div className={styles.benefitBox}>
            <h3 className={styles.benefitTitle}>{t('pricing.benefits.title', { defaultValue: '为什么选择AdEff?' })}</h3>
            <p className={styles.benefitSubtitle}>
              {t('pricing.benefits.subtitle', { defaultValue: '购买AdEff，您可以获得以下权益：' })}
            </p>
            <div className={styles.benefitList}>
              {planBenefits.map((desc) => {
                return (
                  <div key={desc} className={styles.benefitRow}>
                    <AIcon className={styles.benefitIcon} type='icon-line-global_check' />
                    <span>{desc}</span>
                  </div>
                )
              })}
            </div>
          </div>
          <div key={planCardsAnimationKey} className={styles.planCardsWrap}>
            <div className={styles.starterCard}>
              <div className={styles.pricingItem}>
                <AIcon type='icon-fill-global_Premium' width={24} height={24} />
                <span className={styles.itemTitle}>
                  <MiddleText text={t('pricing.starter.title')} />
                </span>
              </div>
              {
                isInternal
                  ? <p className={styles.starterDesc}>{t('pricing.starter.desc_2')}</p>
                  : <p className={styles.starterDesc}>{t('pricing.starter.desc_1')}</p>
              }
              <div className={styles.price}>
                {formatCurrency(starterPrice || '-')}
                <div className={styles.discount}>{t('pricing.starter.discount')}</div>
              </div>
              <div className={styles.originPrice}>
                {t('pricing.starter.originPrice')}{starterOriginalPriceText}
              </div>
              <p className={styles.times}>
                <Trans
                  i18nKey={isInternal ? 'pricing.starter.timesDesc1' : 'pricing.starter.timesDesc'}
                  values={{ count: starterAnalyseCount || '-' }}
                  components={{
                    strong: <span className={styles.cnt} />,
                  }}
                />
              </p>
              <div className={styles.starterCardDesc}>
                {starterCardDesc.map((desc) => {
                  return (
                    <div key={desc} className={styles.starterCardDescRow}>
                      <span className={styles.starterCardDescDot}></span>
                      <span>{desc}</span>
                    </div>
                  )
                })}
              </div>
              <div
                className={styles.subscribeBtn}
                onClick={ENABLE_FASTSPRING_CHECKOUT ? handleSubscribe : handleContactUs}
                data-testid='pricing-subscribe-btn'
              >
                <span className={styles.subscribeBtnText}>
                  {ENABLE_FASTSPRING_CHECKOUT && !isInternal && !isQuickPricing
                    ? t('pricing.subscribeBtn')
                    : t('pricing.contactUs')}
                </span>
                <AIcon className={styles.subscribeBtnArrow} type='icon-line-global_arrow'></AIcon>
              </div>
            </div>
            <div className={styles.unlimitedCard}>
              <div className={styles.pricingItem}>
                <AIcon type='icon-fill-global_Unlimited' width={24} height={24} />
                <span className={styles.itemTitle}>
                  <MiddleText text={t('pricing.unlimited.title')} />
                </span>
              </div>
              <p className={styles.premiumDesc}>
                {unlimitedPlanDesc}
              </p>
              <div className={styles.price}>{formatCurrency(unlimitedPrice || '-')}</div>
              <p className={styles.times}>
                <Trans
                  i18nKey='pricing.starter.timesDesc1'
                  values={{ count: unlimitedTestCount || '-' }}
                  components={{
                    strong: <span className={styles.cnt} />,
                  }}
                />
              </p>
              <div className={styles.unlimitedCardDesc}>
                {unlimitedCardDesc.map((desc) => {
                  return (
                    <div key={desc} className={styles.unlimitedCardDescRow}>
                      <span className={styles.unlimitedCardDescDot}></span>
                      <span>{desc}</span>
                    </div>
                  )
                })}
              </div>
              <div className={styles.subscribeBtn} onClick={handleContactUs} data-testid='pricing-contact-us-btn'>
                <span className={styles.subscribeBtnText}>{t('pricing.contactUs')}</span>
                <AIcon className={styles.subscribeBtnArrow} type='icon-line-global_arrow'></AIcon>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.customPlanSection}>
          <p className={styles.customPlanTitle}>{t('pricing.benefits.moreNeedsTitle', { defaultValue: '有其他购买需求？' })}</p>
          <p className={classNames(styles.customPlanDesc, { [styles.ja]: isJaLocale })}>
            {customPlanPrefix}
            <span className={styles.customPlanEmail}> Adeff@mininglamp.com </span>
            {customPlanSuffix}
          </p>
        </div>
      </div>
    </div>
  )
}

export default observer(PricingPagePc)
